import { Suspense, lazy, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { MotionConfig } from 'motion/react';
import { getErrorReporter } from '@/lib/error-reporter';
import { ThemeProvider } from 'next-themes';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  matchPath,
  useLocation,
  useMatch,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6';
import { NUQS_ADAPTER_DEFAULT_OPTIONS } from '@/lib/nuqs-adapter-options';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TooltipProvider } from '@reflex/ui/components/ui/tooltip';
import { Button } from '@reflex/ui/components/ui/button';
import { Toaster } from '@reflex/ui/components/ui/sonner';
import { PanelTitle } from '@reflex/ui/components/layout/Typography';
import { PluginClientProvider, PluginRegistryProvider } from '@reflex/ui/client/context';
import { AppShell } from '@/components/layout/Layout';
import { ConfigureShell, isConfigurePluginRoutePath } from '@/components/layout/ConfigureShell';
import {
  AccountShell,
  AutomationsShell,
  ConnectShell,
  LabsShell,
  accountExtraFirstSegments,
  isAccountPluginRoutePath,
  isAutomationsPluginRoutePath,
  isConnectPluginRoutePath,
  isLabsPluginRoutePath,
  labsExtraFirstSegments,
} from '@/components/layout/TakeoverShell';
import { RouteSuspenseTracker, TopProgressBar } from '@/components/layout/TopProgressBar';
import { MarketingLayout } from '@reflex/plugin-marketing/web/MarketingLayout';
import { AgentsPage } from '@/pages/agents/AgentsPage';

// Lazy-load docs route modules to avoid pulling them into the main bundle.
// These are wrapped in Suspense at their render sites.
const DocsLayout = lazy(() =>
  import('@reflex/plugin-docs/web/DocsLayout').then((m) => ({ default: m.DocsLayout })),
);
const DocsPage = lazy(() =>
  import('@reflex/plugin-docs/web/DocsPage').then((m) => ({ default: m.DocsPage })),
);

// Lazy-load demos route modules to avoid pulling them into the main bundle.
// DemosIndexPage renders inside DocsLayout at /docs/demos; /demos redirects there.
const DemosIndexPage = lazy(() =>
  import('@reflex/plugin-guided-demos/web/DemosIndexPage').then((m) => ({
    default: m.DemosIndexPage,
  })),
);
const DemoDetailPage = lazy(() =>
  import('@/pages/demos/DemoDetailPage').then((m) => ({
    default: m.DemoDetailPage,
  })),
);

import { AdminShell } from '@/components/layout/AdminShell';
import { AdminUsersPage } from '@/pages/admin/AdminUsersPage';
import { AdminUserDetailPage } from '@/pages/admin/AdminUserDetailPage';
import { AdminOrganizationsPage } from '@/pages/admin/AdminOrganizationsPage';
import { AdminOrganizationDetailPage } from '@/pages/admin/AdminOrganizationDetailPage';
import { AdminBillingEntityDetailPage } from '@/pages/admin/AdminBillingEntityDetailPage';
import { AdminCompaniesPage } from '@/pages/admin/AdminCompaniesPage';
import { AdminFlagsPage } from '@/pages/admin/AdminFlagsPage';
import { AdminAuthPage } from '@/pages/admin/AdminAuthPage';
import { AdminAppearancePage } from '@/pages/admin/AdminAppearancePage';
import { AdminMaintenancePage } from '@/pages/admin/AdminMaintenancePage';
import { AdminServiceSettingsPage } from '@/pages/admin/AdminServiceSettingsPage';
import { AdminEmailSettingsPage } from '@/pages/admin/AdminEmailSettingsPage';
import { AdminPluginsPage } from '@/pages/admin/AdminPluginsPage';
import { AdminPluginDetailPage } from '@/pages/admin/AdminPluginDetailPage';
import { AdminPluginSetupPage } from '@/pages/admin/AdminPluginSetupPage';
import { ColorThemeProvider } from '@reflex/plugin-themes/web/ColorThemeProvider';
import { useAccountPreferencesSync } from '@/hooks/user/useAccountPreferencesSync';
import { AdminWebhooksPage } from '@/pages/admin/AdminWebhooksPage';
import { ServiceSecretsPage } from '@/pages/admin/secrets/ServiceSecretsPage';
import { AgentsHubPage } from '@/pages/org/agents/AgentsHubPage';
import { SecretStorageDetailPage } from '@/pages/admin/secrets/SecretStorageDetailPage';
import { BlueprintsPage } from '@/pages/blueprints/BlueprintsPage';
import { BaseImageRedirect } from '@/pages/blueprints/BaseImageRedirect';
import { LoginPage } from '@/pages/auth/LoginPage';
import { AuthScreen } from '@/pages/auth/AuthScreen';
import { InviteLandingPage } from '@/pages/invite/InviteLandingPage';
import { SelectOrganizationPage } from '@/pages/oauth/SelectOrganizationPage';
import { AuthorizePage } from '@/pages/oauth/AuthorizePage';
import { ConnectDevicePage } from '@/pages/connect/ConnectDevicePage';
import { SetupPage } from '@/pages/setup/SetupPage';
import { OrgSetupPage } from '@/pages/org-setup/OrgSetupPage';
import { OrgSetupWelcome } from '@/pages/org-setup/OrgSetupWelcome';
import { WaitingForOwner } from '@/components/auth/WaitingForOwner';
import { ProfilePage } from '@/pages/user/ProfilePage';
import { SettingsPage } from '@/pages/user/SettingsPage';
import { CredentialsRedirect } from '@/pages/security/CredentialsRedirect';
import { UserSecretsRedirect } from '@/pages/security/UserSecretsRedirect';
import { UserOnboardingPage } from '@/pages/user-onboarding/UserOnboardingPage';
import { InboxPage } from '@/pages/inbox/InboxPage';
import { IntegrationsPage } from '@/pages/connect/IntegrationsPage';
import { McpHubPage } from '@/pages/connect/McpHubPage';
import { CredentialsHubPage } from '@/pages/connect/CredentialsHubPage';
import { SharingHubPage } from '@/pages/connect/SharingHubPage';
import { MiscOverviewPage } from '@/pages/resources/MiscOverviewPage';
import { PluginsOverviewPage } from '@/pages/plugins/PluginsOverviewPage';
import { PluginDetailPage } from '@/pages/plugins/PluginDetailPage';
import { MonitorPage } from '@/pages/monitor/MonitorPage';
import { MonitorOrgDetailPage } from '@/pages/monitor/MonitorOrgDetailPage';
import { OrganizationOverviewPage } from '@/pages/org/OrganizationOverviewPage';
import { OrganizationMembersPage } from '@/pages/org/OrganizationMembersPage';
import { SharingSettingsPage } from '@/pages/org/SharingSettingsPage';
import { OrganizationTeamsPage } from '@/pages/org/OrganizationTeamsPage';
import { TeamSecretsPage } from '@/pages/org/team-secrets/TeamSecretsPage';
import { TeamModelProvidersPage } from '@/pages/org/team-model-providers/TeamModelProvidersPage';
import { SecurityOverviewPage } from '@/pages/security/SecurityOverviewPage';
import { OrganizationRolesPage } from '@/pages/org/OrganizationRolesPage';
import { CompanyPage } from '@/pages/org/company/CompanyPage';
import { OrganizationHubPage } from '@/pages/org/organization/OrganizationHubPage';
import { NotFoundPage } from '@/pages/errors/NotFoundPage';
import { AuthContext, useAuth, useAuthProvider } from '@/hooks/auth/useAuth';
import { useFirstSignInToast } from '@/hooks/auth/useFirstSignInToast';
import { useAuthLinkOutcomeToast } from '@/hooks/auth/useAuthLinkOutcomeToast';
import { RequireSuperAdmin } from '@/components/auth/RequireSuperAdmin';
import { AccountDisabledScreen } from '@/components/auth/AccountDisabledScreen';
import { OrganizationDisabledScreen } from '@/components/auth/OrganizationDisabledScreen';
import { TrialExpiredScreen } from '@/components/auth/TrialExpiredScreen';
import { KeyInvalidScreen } from '@/components/auth/KeyInvalidScreen';
import { useSandboxHealth } from '@/hooks/org/useSandboxHealth';
import { isUsableSandboxHealth, resolveSandboxGateDecision } from '@/lib/sandbox-gate';
import { OrgSlugResolver } from '@/components/auth/OrgSlugResolver';
import { OrgRootRedirect } from '@/components/auth/OrgRootRedirect';
import { connectProviderPopup } from '@/lib/auth-provider-connect';
import { LegacyRedirect } from '@/components/auth/LegacyRedirect';
import { LegacyOrgPathRedirect } from '@/components/auth/LegacyOrgPathRedirect';
import { usePlugins } from '@/hooks/config/usePlugins';
import {
  getStaticDocsPublicRoutes,
  getStaticPublicRoutes,
  getStaticChromelessPublicRoutes,
  getStaticHomePage,
  resolveOrgDisabledCurePaths,
} from '@/lib/plugin-registry';
import {
  asRelativePath,
  COMPANY_SECURITY_PATH,
  LEGACY_ENTERPRISE_AUTHORIZATION_PATH,
  LEGACY_ROOT_REDIRECT_PATHS,
  ORG_PATH_PREFIX,
  ROUTES,
  withOrgSlug,
} from '@/lib/routes';
import {
  createApiAdapter,
  createAuthAdapter,
  createRouterAdapter,
  createSocketAdapter,
} from '@/lib/plugin-collaborators';
import { PluginComponentBridge } from '@/components/layout/PluginComponentBridge';

// Mounted directly inside the RequireSuperAdmin-gated /admin section rather than
// via the plugin route system, because plugin routes are mounted under
// orgs/:orgSlug and these admin pages must live at root /admin/<page>. Lazy so the plugin
// bundles only load when a super admin opens the page.
const DatabasePage = lazy(() =>
  import('@reflex/plugin-db-explorer/web/DatabasePage').then((m) => ({
    default: m.DatabasePage,
  })),
);
const SeedsPage = lazy(() =>
  import('@reflex/plugin-seeds/web/SeedsPage').then((m) => ({
    default: m.SeedsPage,
  })),
);
const AdminNotificationsPage = lazy(() =>
  import('@reflex/plugin-notifications/web/AdminNotificationsPage').then((m) => ({
    default: m.AdminNotificationsPage,
  })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: true,
    },
  },
});

function AuthProvider({ children }: { children: ReactNode }) {
  const auth = useAuthProvider();
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

function PluginClientBridge({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const plugins = usePlugins();
  const location = useLocation();
  const orgRouteMatch = useMatch(`/${ORG_PATH_PREFIX}/:orgSlug/*`);
  const params = useParams();
  const navigate = useNavigate();

  const api = useMemo(() => createApiAdapter(), []);
  const socket = useMemo(() => createSocketAdapter(), []);
  const authAdapter = useMemo(() => createAuthAdapter(auth), [auth]);
  const router = useMemo(() => {
    const matchedOrgSlug = orgRouteMatch?.params.orgSlug;
    const validOrgSlug = auth.memberships.some((m) => m.organization.slug === matchedOrgSlug)
      ? matchedOrgSlug
      : undefined;
    return createRouterAdapter(
      { orgSlug: params.orgSlug ?? validOrgSlug, ...params },
      location.pathname,
      navigate,
    );
  }, [auth.memberships, location.pathname, navigate, orgRouteMatch?.params.orgSlug, params]);
  // Built (and memoized) in usePlugins beside the manifest resolve, so the host
  // can't drop a field it forwards to the plugin context.
  const registry = plugins.pluginRegistry;

  return (
    <PluginClientProvider auth={authAdapter} router={router} socket={socket} api={api}>
      <PluginRegistryProvider value={registry}>
        <PluginComponentBridge>{children}</PluginComponentBridge>
      </PluginRegistryProvider>
    </PluginClientProvider>
  );
}

function AuthedShell() {
  useFirstSignInToast();
  useAuthLinkOutcomeToast();
  return (
    <BasicAuthGate>
      <OrgGate>
        <AppShell />
      </OrgGate>
    </BasicAuthGate>
  );
}

/**
 * Sibling of {@link AuthedShell} used for the pre-app gate wizards
 * (`/org-setup/new`, `/orgs/:orgSlug/setup`). Renders the matching route's
 * outlet inside its own full-viewport `WizardLayout` — crucially WITHOUT
 * `AppShell`, so the host sidebar can't peek through or be navigated
 * away from while the wizard is incomplete.
 */
function AuthedWizardShell() {
  return (
    <BasicAuthGate>
      <Outlet />
    </BasicAuthGate>
  );
}

/**
 * The "you must be signed in and past phase 1" portion of the gate.
 * Shared between {@link AuthedShell} and {@link AuthedWizardShell}.
 */
function BasicAuthGate({ children }: { children: ReactNode }) {
  const { authenticated, authEnabled, loading, setupRequired, user } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background text-muted-foreground">
        Loading&hellip;
      </div>
    );
  }

  // The first-run wizard is a hard gate: until setup finalizes, render
  // SetupPage even if the user would otherwise be asked to log in. The
  // server also returns 503 SETUP_REQUIRED for non-setup routes, so this
  // keeps the SPA from attempting to mount features that can't load yet.
  if (setupRequired) {
    return <SetupPage />;
  }

  if (authEnabled && !authenticated) {
    return <LoginPage />;
  }

  // Disabled accounts see a contact-support screen instead of the app
  // shell. The server also rejects every authenticated API call from
  // this user with 403 `account_disabled`, so even if this gate were
  // bypassed nothing would load. Sits below the `!authenticated` branch
  // so signed-out users still get the login page.
  if (authEnabled && user?.status === 'disabled') {
    return <AccountDisabledScreen />;
  }

  return children;
}

/**
 * Phase 2 routing — the "do you have a real org, and is it
 * bootstrapped?" checks. Only mounted under {@link AuthedShell}
 * (sidebar-bearing routes); the wizard shell skips it because the
 * wizard routes are the redirect target.
 */
function OrgGate({ children }: { children: ReactNode }) {
  const {
    memberships,
    activeMembership,
    orgsNeedingBootstrap,
    isPlatformAdmin,
    can,
    ssoRequirements = [],
    refreshSession,
  } = useAuth();
  const location = useLocation();
  const { manifests } = usePlugins();

  const activeOrg = activeMembership?.organization ?? null;
  const unsatisfiedSso = activeOrg
    ? ssoRequirements.find(
        (requirement) => requirement.organizationId === activeOrg.id && !requirement.satisfied,
      )
    : undefined;
  const [connectingSso, setConnectingSso] = useState(false);

  // Sandbox key health is only meaningful for a bootstrapped, enabled org.
  // Gate the fetch so we never probe Runloop during phase-1/2 setup or for a
  // disabled org. The hook must run unconditionally (rules-of-hooks), so the
  // `enabled` flag — not an early return — controls whether it fires.
  const sandboxHealthEnabled =
    !!activeOrg && activeOrg.disabledReason == null && activeOrg.bootstrappedAt !== null;
  const sandboxHealth = useSandboxHealth(activeOrg?.id, sandboxHealthEnabled);

  // Phase 2 gate: user has zero org memberships → force them onto the
  // `/org-setup` decision screen so they pick **join** vs **create**
  // before spinning up a duplicate org. The decision screen is the
  // single deliberate anti-duplicate guard; both `/org-setup` (the
  // decision) and `/org-setup/new` (the create form) are permitted so
  // the second can be linked from the first.
  if (memberships.length === 0) {
    if (location.pathname !== '/org-setup' && location.pathname !== '/org-setup/new') {
      return <Navigate to="/org-setup" replace />;
    }
    return children;
  }

  // Phase 2 gate: the active org is blocked. Replace the org shell with a
  // blocking screen — the "trial has ended" variant when a subscription
  // window has lapsed (`disabledReason === 'subscription_expired'`), the
  // payment-required variant for a billing suspension (`'nonpayment'`),
  // otherwise the generic contact-support / switch-org screen for a manual
  // disable. All offer a switcher to the member's other usable orgs. The
  // server computes `disabledReason` from the manual status, the stored
  // disable reason, and the subscription window, and rejects every
  // authenticated API call whose active org resolves to a blocked org with
  // 403 `organization_disabled`, so the underlying pages couldn't load
  // anyway. (An org with an active or absent subscription has
  // `disabledReason == null` and falls through to the app.)
  //
  // Cure-path exception: a plugin route can declare
  // `availableWhenOrgDisabled` for the active reason (the billing page for
  // `nonpayment` / `subscription_expired`), in which case the current
  // location renders normally so the org can pay its way back in. The
  // matching server routes carry the same flag on their rbac config, so
  // only the cure surface's API calls survive the 403 gate.
  if (activeOrg && activeOrg.disabledReason != null) {
    const reason = activeOrg.disabledReason;
    const curePaths = resolveOrgDisabledCurePaths(manifests, reason);
    const cureFullPaths = curePaths.map((path) => withOrgSlug(activeOrg.slug, path));
    // Exact route matching (not a string prefix): only the route that
    // declared `availableWhenOrgDisabled` renders. A sibling like
    // `/org/billing/usage` did not opt in, and its API calls would 403 —
    // it gets the blocking screen like everything else.
    if (cureFullPaths.some((path) => matchPath({ path, end: true }, location.pathname))) {
      return children;
    }
    const billingPath = cureFullPaths[0] ?? null;
    return reason === 'subscription_expired' ? (
      <TrialExpiredScreen billingPath={billingPath} />
    ) : reason === 'nonpayment' ? (
      <OrganizationDisabledScreen variant="nonpayment" billingPath={billingPath} />
    ) : (
      <OrganizationDisabledScreen />
    );
  }

  if (unsatisfiedSso && !isPlatformAdmin) {
    const connect = async () => {
      setConnectingSso(true);
      try {
        await connectProviderPopup(
          'oidc',
          'link',
          `/auth/oidc/realm-link-start?org=${encodeURIComponent(unsatisfiedSso.organizationSlug)}`,
        );
      } finally {
        await refreshSession();
        setConnectingSso(false);
      }
    };
    return (
      <OrganizationDisabledScreen
        variant="sso"
        onContinueWithSso={() => void connect()}
        connectingSso={connectingSso}
      />
    );
  }

  // Phase 2 gate: the active org has never been bootstrapped. The
  // `null` check is strict — fixtures and legacy clients without the
  // field (`undefined`) bypass the gate so older test harnesses
  // continue to work. Permitted users (org:manage or super-admin) get
  // bounced into the per-org wizard; others see a placeholder.
  if (activeOrg && activeOrg.bootstrappedAt === null) {
    const setupPath = ROUTES.org(activeOrg.slug).setup;
    if (location.pathname.startsWith(setupPath)) return children;
    if (orgsNeedingBootstrap.includes(activeOrg.id) || isPlatformAdmin) {
      return <Navigate to={setupPath} replace />;
    }
    return <WaitingForOwner orgName={activeOrg.name} />;
  }

  // Phase 2 gate: the org is bootstrapped but its stored Runloop key may have
  // been revoked / removed since. Without this, every sandbox-backed page
  // would fail at point-of-use with no guided recovery. Re-route a user who
  // can manage the org into the wizard's Runloop step to re-enter a working
  // key (validated against Runloop); others see a contact-an-admin screen.
  if (activeOrg && sandboxHealthEnabled) {
    const setupPath = ROUTES.org(activeOrg.slug).setup;
    // Block on the first resolution only; the result is cached (`staleTime`),
    // so subsequent navigations don't re-show this loader or flash the app.
    if (sandboxHealth.isLoading && !sandboxHealth.data) {
      return (
        <div className="flex min-h-dvh items-center justify-center bg-background text-muted-foreground">
          Loading&hellip;
        </div>
      );
    }
    const decision = resolveSandboxGateDecision({
      status: sandboxHealth.data?.status,
      canManage: can('org:manage'),
      onSetupPath: location.pathname.startsWith(setupPath),
    });
    if (decision === 'redirect-setup') {
      return <Navigate to={`${setupPath}/runloop`} replace />;
    }
    if (decision === 'key-invalid-screen') {
      return <KeyInvalidScreen orgName={activeOrg.name} />;
    }
  }

  return children;
}

/**
 * Route element for `/orgs/:orgSlug/setup` when mounted on the wizard shell
 * (no `OrgSlugResolver` parent in scope). Resolves the URL slug to a
 * membership and syncs `activeOrganizationId` so the wizard's authed
 * fetches scope to the correct organization.
 */
function OrgSetupExistingRoute() {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const { memberships, activeOrganizationId, setActiveOrganizationId } = useAuth();

  const match = memberships.find((m) => m.organization.slug === orgSlug) ?? null;
  const targetOrgId = match?.organization.id ?? null;

  // Share the gate's sandbox key-health read (same React Query key) so the
  // bounce-out decision below stays consistent with what sent the user here.
  // Only enabled for a bootstrapped org — an un-bootstrapped one belongs in
  // the wizard regardless of key health.
  const isBootstrapped = !!match && match.organization.bootstrappedAt !== null;
  const sandboxHealth = useSandboxHealth(targetOrgId, isBootstrapped);

  // Keep the latest active org in a ref so the sync effect below can read it
  // WITHOUT depending on it. If `activeOrganizationId` were an effect dep, the
  // effect would re-run when you switch *away* from this org — and because the
  // wizard route can stay briefly mounted during the navigation (Suspense on
  // the destination route keeps the old tree alive), it would immediately
  // re-assert this org as active and bounce you back to its setup wizard.
  const activeOrgIdRef = useRef(activeOrganizationId);
  useEffect(() => {
    activeOrgIdRef.current = activeOrganizationId;
  }, [activeOrganizationId]);

  // Sync the active org to the URL's org, but ONLY when the URL's org changes.
  // `setActiveOrganizationId` has a stable identity, so this fires on real
  // slug navigation — not on incidental active-org churn.
  useEffect(() => {
    if (targetOrgId && targetOrgId !== activeOrgIdRef.current) {
      setActiveOrganizationId(targetOrgId);
    }
  }, [targetOrgId, setActiveOrganizationId]);

  // Self-heal a stale entry into the wizard. `OrgGate` sends a user here when
  // `bootstrappedAt === null` (initial setup) OR when a bootstrapped org's
  // Runloop key is unhealthy (key repair). Only bounce back into the app when
  // the org is bootstrapped AND its key is usable again — otherwise a
  // bootstrapped-but-broken-key org would bounce out, the gate would bounce
  // it straight back, and the two would loop. While health is still loading
  // we render the wizard rather than bouncing, so a manager mid-repair isn't
  // flashed out and back.
  if (isBootstrapped && isUsableSandboxHealth(sandboxHealth.data?.status)) {
    return <Navigate to={ROUTES.org(match!.organization.slug).home} replace />;
  }

  return <OrgSetupPage mode="existing" />;
}

/**
 * Backwards-compatible alias used by chromeless plugin routes that
 * already wrap themselves in `<AuthGate>` (they assume sidebar-less
 * full-viewport behaviour). Delegates to {@link BasicAuthGate}.
 */
function AuthGate({ children }: { children: ReactNode }) {
  return <BasicAuthGate>{children}</BasicAuthGate>;
}

/**
 * Route element for `/`. Anonymous visitors get the plugin-contributed
 * marketing home page (`PluginWebManifest.homePage`, resolved statically
 * like the other public plugin surfaces); when no composed plugin
 * contributes one, they get the sign-in screen exactly as before.
 *
 * Everyone else is sent to bare `/orgs`, whose route sits on
 * {@link AuthedShell} — so the signed-in path keeps every gate it had
 * when `/` itself lived on the authed shell: the disabled-account
 * screen, the zero-membership redirect to `/org-setup`, org disabled /
 * bootstrap checks, and the final `OrgRootRedirect` into the active
 * org. The crawler-facing HTML for this URL is rendered server-side
 * (see plugin-marketing's `routes.home.ts`).
 */
// Resolved once at module scope: the static registry is frozen at bundle
// init (see plugin-registry.ts), so the component reference never changes —
// and react-hooks/static-components rightly wants it out of render.
const StaticHomePage = getStaticHomePage();

function RootIndexRoute() {
  const { authenticated, authEnabled, loading, setupRequired } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background text-muted-foreground">
        Loading&hellip;
      </div>
    );
  }

  // First-run wizard is a hard gate, same as BasicAuthGate.
  if (setupRequired) {
    return <SetupPage />;
  }

  if (authEnabled && !authenticated) {
    if (StaticHomePage) {
      return (
        <Suspense fallback={<RouteSuspenseTracker />}>
          <StaticHomePage />
        </Suspense>
      );
    }
    return <LoginPage />;
  }

  return <Navigate to={`/${ORG_PATH_PREFIX}`} replace />;
}

/**
 * Route element for `/home`. Renders the same plugin-contributed home
 * page {@link RootIndexRoute} mounts at `/` — resolved through
 * `getStaticHomePage()` so a deployment that overrides the contribution
 * by priority overrides both URLs together — but with no session
 * branch: a signed-in user gets the marketing page here rather than the
 * redirect into their organization.
 *
 * With no contribution there is no page to alias, so this forwards to
 * `/`, which owns the sign-in fallback.
 */
function HomeAliasRoute() {
  if (!StaticHomePage) {
    return <Navigate to={ROUTES.root} replace />;
  }
  return (
    <Suspense fallback={<RouteSuspenseTracker />}>
      <StaticHomePage />
    </Suspense>
  );
}

interface ResolvedPluginRoute {
  path: string;
  component: React.ComponentType<Record<string, unknown>>;
  config?: Record<string, unknown>;
}

/**
 * Renders the docs route children (index page, slug page, and plugin-contributed
 * docs routes). Separated into its own function so the route table is defined
 * in one place.
 */
function renderDocsRouteChildren(
  staticDocsPublic: ResolvedPluginRoute[],
  dynamicDocsPublicRoutes: ResolvedPluginRoute[],
) {
  const coreDocsPaths = new Set(['/docs', '/docs/*', '/docs/demos', '/docs/demos/:demoSlug']);
  return (
    <>
      {/* /docs renders the overview page as the docs landing */}
      <Route
        path="/docs"
        element={
          <Suspense fallback={<RouteSuspenseTracker />}>
            <DocsPage />
          </Suspense>
        }
      />
      {/* Demos catalog renders inside the docs shell */}
      <Route
        path="/docs/demos"
        element={
          <Suspense fallback={<RouteSuspenseTracker />}>
            <DemosIndexPage />
          </Suspense>
        }
      />
      {/* PRD-G14: Individual demo pages render inside the docs shell */}
      <Route
        path="/docs/demos/:demoSlug"
        element={
          <Suspense fallback={<RouteSuspenseTracker />}>
            <DemoDetailPage />
          </Suspense>
        }
      />
      <Route
        path="/docs/*"
        element={
          <Suspense fallback={<RouteSuspenseTracker />}>
            <DocsPage />
          </Suspense>
        }
      />
      {staticDocsPublic
        .filter((r) => !coreDocsPaths.has(r.path))
        .map(({ path, component: Component, config }) => (
          <Route
            key={`static-docs-public-${path}`}
            path={path}
            element={
              <Suspense fallback={<RouteSuspenseTracker />}>
                <Component {...(config ?? {})} />
              </Suspense>
            }
          />
        ))}
      {dynamicDocsPublicRoutes.map(({ path, component: Component, config }) => (
        <Route
          key={`docs-public-${path}`}
          path={path}
          element={
            <Suspense fallback={<RouteSuspenseTracker />}>
              <Component {...(config ?? {})} />
            </Suspense>
          }
        />
      ))}
    </>
  );
}

/**
 * Standalone docs route branch. Renders DocsLayout (standalone docs content + footer)
 * with DocsContent inside. Entirely separate from the main app route tree so
 * `/docs/*` paths never compete with the dynamic pre-`/orgs` compat
 * catch-all (`:legacyOrgSlug/*`) in React Router's ranking algorithm.
 *
 * Wraps content in `PluginComponentBridge` so the guided demo simulation
 * (which renders `EmbeddableReplay` → `AgentStreamView`) has access to the
 * registered component implementations. The bridge itself doesn't call
 * `usePlugins()` — only the rendered components do, and `/api/config/plugins`
 * is a setup-public endpoint accessible to anonymous visitors.
 */
function DocsPublicRoutes() {
  const staticDocsPublic = getStaticDocsPublicRoutes();
  return (
    <PluginComponentBridge>
      <Routes>
        <Route
          element={
            <Suspense fallback={<RouteSuspenseTracker />}>
              <DocsLayout />
            </Suspense>
          }
        >
          {renderDocsRouteChildren(staticDocsPublic, [])}
        </Route>
      </Routes>
    </PluginComponentBridge>
  );
}

/** `/admin/billing-entities/:id` → `/admin/companies/:id` */
function LegacyAdminBillingEntityRedirect() {
  const { billingEntityId } = useParams<{ billingEntityId: string }>();
  return <Navigate to={ROUTES.admin.company(billingEntityId ?? '')} replace />;
}

/**
 * Redirect `/orgs/:orgSlug/sentry/:projectId` (the legacy integration
 * route) to the new
 * `/orgs/:orgSlug/configure/connect/integrations/sentry/:projectId`
 * location.
 */
function LegacySentryProjectRedirect() {
  const { projectId } = useParams<{ projectId: string }>();
  return <Navigate to={`../configure/connect/integrations/sentry/${projectId ?? ''}`} replace />;
}

/**
 * Redirect `/orgs/:orgSlug/plugins/:pluginName` (the legacy Labs detail
 * route) to Connect → Integrations plugin detail.
 */
function LegacyPluginDetailRedirect() {
  const { pluginName } = useParams<{ pluginName: string }>();
  return <Navigate to={`../configure/connect/plugins/${pluginName ?? ''}`} replace />;
}

/** `/orgs/:slug/org/plugins/:name` → `/orgs/:slug/configure/connect/plugins/:name` */
function LegacyOrgPluginDetailRedirect() {
  const { pluginName } = useParams<{ pluginName: string }>();
  return <Navigate to={`../configure/connect/plugins/${pluginName ?? ''}`} replace />;
}

// ── Configure migration redirects for parameterized routes ──────────

/** `/orgs/:slug/blueprints/:id` → `/orgs/:slug/configure/build/environments/:id` */
function LegacyBlueprintRedirect() {
  const { blueprintId } = useParams<{ blueprintId: string }>();
  return <Navigate to={`../configure/build/environments/${blueprintId ?? ''}`} replace />;
}

/** `/orgs/:slug/org/teams/:id` → `/orgs/:slug/configure/people/teams/:id` */
function LegacyTeamDetailRedirect() {
  const { teamId } = useParams<{ teamId: string }>();
  return <Navigate to={`../configure/people/teams/${teamId ?? ''}`} replace />;
}

/** `/orgs/:slug/org/teams/:id/secrets` → `/orgs/:slug/configure/people/teams/:id/secrets` */
function LegacyTeamSecretsRedirect() {
  const { teamId } = useParams<{ teamId: string }>();
  return <Navigate to={`../configure/people/teams/${teamId ?? ''}/secrets`} replace />;
}

/** `/orgs/:slug/org/teams/:id/model-providers` → same under configure */
function LegacyTeamModelProvidersRedirect() {
  const { teamId } = useParams<{ teamId: string }>();
  return <Navigate to={`../configure/people/teams/${teamId ?? ''}/model-providers`} replace />;
}

/** `/orgs/:slug/org/roles/:id` → `/orgs/:slug/configure/people/roles/:id` */
function LegacyRoleDetailRedirect() {
  const { roleId } = useParams<{ roleId: string }>();
  return <Navigate to={`../configure/people/roles/${roleId ?? ''}`} replace />;
}

/** `/orgs/:slug/org/service-accounts/:id` → same under configure */
function LegacyServiceAccountDetailRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`../configure/organization/service-accounts/${id ?? ''}`} replace />;
}

/** `/orgs/:slug/org/costs/users/:userId` → model-costs detail */
function LegacyCostsUserRedirect() {
  const { userId } = useParams<{ userId: string }>();
  return <Navigate to={`../configure/usage/model-costs/users/${userId ?? ''}`} replace />;
}

/** `/orgs/:slug/org/costs/keys/:secretId` (+ optional /records[/category]) */
function LegacyCostsKeyRedirect() {
  const { secretId, category } = useParams<{ secretId: string; category?: string }>();
  const { pathname } = useLocation();
  const wantsRecords = pathname.includes('/records');
  const base = `../configure/usage/model-costs/keys/${secretId ?? ''}`;
  if (!wantsRecords) return <Navigate to={base} replace />;
  if (category) return <Navigate to={`${base}/records/${category}`} replace />;
  return <Navigate to={`${base}/records`} replace />;
}

/** `/orgs/:slug/org/costs/providers/:provider` */
function LegacyCostsProviderRedirect() {
  const { provider } = useParams<{ provider: string }>();
  return <Navigate to={`../configure/usage/model-costs/providers/${provider ?? ''}`} replace />;
}

/** `/orgs/:slug/org/costs/categories/:category` */
function LegacyCostsCategoryRedirect() {
  const { category } = useParams<{ category: string }>();
  return <Navigate to={`../configure/usage/model-costs/categories/${category ?? ''}`} replace />;
}

/** `/orgs/:slug/agent-personas/:id` (+ optional /edit) */
function LegacyAgentPersonaDetailRedirect() {
  const { id } = useParams<{ id: string }>();
  const { pathname } = useLocation();
  const suffix = pathname.endsWith('/edit') ? '/edit' : '';
  return <Navigate to={`../configure/build/agents/personas/${id ?? ''}${suffix}`} replace />;
}

/** `/orgs/:slug/org/integrations/sentry/:projectId` */
function LegacyOrgSentryProjectRedirect() {
  const { projectId } = useParams<{ projectId: string }>();
  return <Navigate to={`../configure/connect/integrations/sentry/${projectId ?? ''}`} replace />;
}

/** `/orgs/:slug/org/integrations/:provider` → plugin detail */
function LegacyOrgIntegrationRedirect() {
  const { provider } = useParams<{ provider: string }>();
  const { search } = useLocation();
  return <Navigate to={`../configure/connect/plugins/${provider ?? ''}${search}`} replace />;
}

/** `/orgs/:slug/configure/connect/integrations/:provider` → plugin detail */
function ConnectIntegrationPluginRedirect() {
  const { provider } = useParams<{ provider: string }>();
  const { search } = useLocation();
  return <Navigate to={`../plugins/${provider ?? ''}${search}`} replace />;
}

/**
 * Helper component for redirecting `/demos` index to `/docs/demos`.
 * Preserves query params through the redirect.
 */
function DemosIndexRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/docs/demos${search}`} replace />;
}

/**
 * Helper component for redirecting `/demos/:slug` to `/docs/demos/:slug`.
 * Preserves query params (e.g. ?mode=live) through the redirect.
 */
function DemoSlugRedirect() {
  const { demoSlug } = useParams<{ demoSlug: string }>();
  const { search } = useLocation();
  return <Navigate to={`/docs/demos/${demoSlug ?? ''}${search}`} replace />;
}

/**
 * Redirect routes for `/demos` paths. The canonical URLs are under `/docs/demos`
 * where demos render inside the docs shell (PRD-G14/G16). These redirects keep
 * old `/demos` bookmarks and links working.
 */
function DemosRedirectRoutes() {
  return (
    <Routes>
      <Route path="/demos" element={<DemosIndexRedirect />} />
      <Route path="/demos/:demoSlug" element={<DemoSlugRedirect />} />
    </Routes>
  );
}

/**
 * Main app routes (everything except `/docs` and `/demos`). Contains marketing pages,
 * chromeless routes, authenticated routes, and org-scoped routes.
 */
function MainAppRoutes() {
  const {
    routes: pluginRoutes,
    rootLevelRoutes: pluginRootLevelRoutes,
    publicRoutes,
    chromelessAuthedRoutes,
    chromelessPublicRoutes,
    navItems: pluginNavItems,
    isLoading: pluginsLoading,
  } = usePlugins();
  const staticPublic = getStaticPublicRoutes();
  const staticChromelessPublic = getStaticChromelessPublicRoutes();
  const staticPublicPaths = new Set(staticPublic.map((r) => r.path));

  // Org-scoped plugin routes split by surface: config routes mount inside the
  // Configure takeover, the flows surface inside the automations takeover,
  // and the remaining work surfaces stay on the plain app shell.
  const configurePluginRoutes = useMemo(
    () =>
      pluginRoutes.filter(
        (r) =>
          isConfigurePluginRoutePath(r.path) &&
          // Connect routes belong to the Connect takeover, not Configure.
          !isConnectPluginRoutePath(r.path) &&
          // Host hubs own these paths; the plugin still contributes the nav row.
          r.path !== '/configure/connect/mcp' &&
          r.path !== '/configure/build/agents/personas',
      ),
    [pluginRoutes],
  );
  // Company → Security is contributed by plugin-auth-oidc, so in a deployment
  // without that plugin the path is not routable at all. `false` while the
  // manifest is still loading, which is the honest answer then: the `*`
  // fallback below already holds a cold load on RouteSuspenseTracker rather
  // than resolving it early.
  const companySecurityContributed = useMemo(
    () =>
      !pluginsLoading &&
      configurePluginRoutes.some((r) => asRelativePath(r.path) === COMPANY_SECURITY_PATH),
    [configurePluginRoutes, pluginsLoading],
  );
  // Plugin routes under /configure/connect/... belong to the Connect takeover.
  const connectPluginRoutes = useMemo(
    () =>
      pluginRoutes.filter(
        (r) =>
          isConnectPluginRoutePath(r.path) &&
          // Host hubs own these paths; the plugin still contributes the nav row.
          r.path !== '/configure/connect/mcp',
      ),
    [pluginRoutes],
  );
  const automationsPluginRoutes = useMemo(
    () =>
      pluginRoutes.filter(
        (r) => !isConfigurePluginRoutePath(r.path) && isAutomationsPluginRoutePath(r.path),
      ),
    [pluginRoutes],
  );
  // Plugin pages registered under the Misc / Plugins nav groups (e.g.
  // `/town`) belong to the Labs takeover along with the chat surface.
  const labsExtraSegments = useMemo(() => labsExtraFirstSegments(pluginNavItems), [pluginNavItems]);
  const accountExtraSegments = useMemo(
    () => accountExtraFirstSegments(pluginNavItems),
    [pluginNavItems],
  );
  const labsPluginRoutes = useMemo(
    () =>
      pluginRoutes.filter(
        (r) =>
          !isConfigurePluginRoutePath(r.path) &&
          !isAutomationsPluginRoutePath(r.path) &&
          !isAccountPluginRoutePath(r.path, accountExtraSegments) &&
          isLabsPluginRoutePath(r.path, labsExtraSegments),
      ),
    [pluginRoutes, labsExtraSegments, accountExtraSegments],
  );
  // Plugin pages in the `user-menu` group that resolve to an in-app route
  // (e.g. `/tutorials`, `/notifications`) belong to the Account takeover
  // alongside the built-in `/profile` and `/settings` pages.
  const accountPluginRoutes = useMemo(
    () =>
      pluginRoutes.filter(
        (r) =>
          !isConfigurePluginRoutePath(r.path) &&
          !isAutomationsPluginRoutePath(r.path) &&
          !isLabsPluginRoutePath(r.path, labsExtraSegments) &&
          isAccountPluginRoutePath(r.path, accountExtraSegments),
      ),
    [pluginRoutes, labsExtraSegments, accountExtraSegments],
  );
  // Root-level account plugin routes (e.g. `/tutorials`) split out of
  // `pluginRootLevelRoutes` so they mount inside the root-level AccountShell
  // layout route below. Non-account root-level routes (Documentation,
  // Changelog, …) keep mounting on the plain app shell as before.
  const accountRootLevelPluginRoutes = useMemo(
    () =>
      pluginRootLevelRoutes.filter((r) => isAccountPluginRoutePath(r.path, accountExtraSegments)),
    [pluginRootLevelRoutes, accountExtraSegments],
  );
  const nonAccountRootLevelRoutes = useMemo(
    () =>
      pluginRootLevelRoutes.filter((r) => !isAccountPluginRoutePath(r.path, accountExtraSegments)),
    [pluginRootLevelRoutes, accountExtraSegments],
  );
  const workPluginRoutes = useMemo(
    () =>
      pluginRoutes.filter(
        (r) =>
          !isConfigurePluginRoutePath(r.path) &&
          !isAutomationsPluginRoutePath(r.path) &&
          !isLabsPluginRoutePath(r.path, labsExtraSegments) &&
          !isAccountPluginRoutePath(r.path, accountExtraSegments),
      ),
    [pluginRoutes, labsExtraSegments, accountExtraSegments],
  );
  const staticChromelessPublicPaths = new Set(staticChromelessPublic.map((r) => r.path));
  const dynamicPublicRoutes = publicRoutes.filter((r) => !staticPublicPaths.has(r.path));
  const dynamicChromelessPublicRoutes = chromelessPublicRoutes.filter(
    (r) => !staticChromelessPublicPaths.has(r.path),
  );
  return (
    <Routes>
      {/*
       * Public invite landing page — renders before the user signs in
       * so they can see who invited them and what email to use.
       * Redemption is an explicit action: after signing in the user
       * lands back here (via saved return path) and clicks "Accept
       * invite", which posts to `/invites/accept-pending`. We
       * deliberately do NOT auto-claim invites during sign-in so an
       * independent signup with a matching email is never silently
       * dropped into a stranger's organization.
       */}
      <Route path="invites/:token" element={<InviteLandingPage />} />
      {/*
       * Root path. Signed-out visitors see the marketing home page (or
       * the sign-in screen when no plugin contributes one); signed-in
       * users are redirected into their active organization via the
       * authed `/orgs` route. See {@link RootIndexRoute}.
       */}
      <Route path="/" element={<RootIndexRoute />} />
      {/*
       * `/home` — the session-independent alias for the marketing home
       * page. `/` shows it only to anonymous visitors, so this is the URL
       * that lands on it whatever the session state. See
       * {@link HomeAliasRoute}.
       */}
      <Route path={asRelativePath(ROUTES.home)} element={<HomeAliasRoute />} />
      {/*
       * Public MCP OAuth org-picker landing. The API server's OAuth
       * callback hook parks the in-flight auth code and redirects here
       * with a one-time `?selection=` id when the signed-in user has
       * multiple memberships. The page fetches the user's orgs via
       * `GET /api/oauth/select-organization`, lets them pick one, and
       * POSTs the choice back so the server can mint an org-bound auth
       * code and hand control back to the OAuth client (Claude, Cursor,
       * …). Mounted outside `AuthGate` because the user has finished
       * provider auth but does not yet hold a web session.
       */}
      <Route path="oauth/select-organization" element={<SelectOrganizationPage />} />
      {/*
       * Public MCP OAuth approval landing, one step earlier than the picker.
       * `GET /api/oauth/authorize` redirects here with a one-time `?pending=`
       * id instead of bouncing straight to GitHub or Google, so the visitor
       * sees who is asking and the legal consent line before an account
       * exists — the surface that makes `users.legal_accepted_at` an honest
       * record. Continue navigates to `/api/oauth/authorize/continue`, which
       * carries the same id on to the identity provider. Mounted outside
       * `AuthGate`: there is no session yet, by design.
       */}
      <Route path="oauth/authorize" element={<AuthorizePage />} />
      {/*
       * Terminal "connect link" approval page. `reflex-cli` opens this with a
       * short `?code=` and polls the server while the signed-in user confirms
       * the machine and picks an org here; approving mints a personal API key
       * bound to the caller. Unlike the OAuth picker above this sits behind
       * `AuthGate` — the approval binds a real credential, so an
       * unauthenticated visitor logs in first and returns to `/connect?code=`.
       */}
      <Route
        path="connect"
        element={
          <AuthGate>
            <ConnectDevicePage />
          </AuthGate>
        }
      />
      {/*
       * Explicit `/login` route. The sign-in screen is normally rendered
       * by `BasicAuthGate` for unauthenticated visitors, so this route
       * is only reachable when the visitor is already signed in (a
       * bookmark, stale link, or "Sign in with X" landing). In that
       * case `AuthScreen` shows the current identity + a Log out option.
       */}
      <Route path="login" element={<AuthScreen />} />
      {/*
       * Legacy `/signup` route. Sign-in and account creation share the one
       * unified `AuthScreen` (every provider flow creates the account when
       * none exists), so this renders the exact same screen. Kept only so
       * old links and bookmarks keep working; nothing in the app links here.
       */}
      <Route path="signup" element={<AuthScreen />} />
      {staticChromelessPublic.map(({ path, component: Component, config }) => (
        <Route
          key={`static-chromeless-public-${path}`}
          path={path}
          element={
            <Suspense fallback={<RouteSuspenseTracker />}>
              <Component {...(config ?? {})} />
            </Suspense>
          }
        />
      ))}
      <Route element={<MarketingLayout />}>
        {staticPublic.map(({ path, component: Component, config }) => (
          <Route
            key={`static-public-${path}`}
            path={path}
            element={
              <Suspense fallback={<RouteSuspenseTracker />}>
                <Component {...(config ?? {})} />
              </Suspense>
            }
          />
        ))}
      </Route>
      {/*
       * Chromeless public routes render at the root with no AuthGate, no
       * MarketingLayout, no AppShell — just the component on a bare page.
       * Use for full-viewport experiences like a "live" embed view.
       */}
      {dynamicChromelessPublicRoutes.map(({ path, component: Component, config }) => (
        <Route
          key={`chromeless-public-${path}`}
          path={path}
          element={
            <Suspense fallback={<RouteSuspenseTracker />}>
              <Component {...(config ?? {})} />
            </Suspense>
          }
        />
      ))}
      {/*
       * Chromeless authed routes are gated by AuthGate but skip AppShell
       * (no sidebar / nav bar). Render full-viewport behind authentication.
       */}
      {chromelessAuthedRoutes.map(({ path, component: Component, config }) => (
        <Route
          key={`chromeless-${path}`}
          path={path}
          element={
            <AuthGate>
              <Suspense fallback={<RouteSuspenseTracker />}>
                <Component {...(config ?? {})} />
              </Suspense>
            </AuthGate>
          }
        />
      ))}
      {/*
       * Public marketing routes render outside AuthGate/AppShell inside the
       * shared MarketingLayout. Any plugin can opt in by setting `public: true`
       * on a web manifest route.
       */}
      <Route element={<MarketingLayout />}>
        {dynamicPublicRoutes.map(({ path, component: Component, config }) => (
          <Route
            key={`public-${path}`}
            path={path}
            element={
              <Suspense fallback={<RouteSuspenseTracker />}>
                <Component {...(config ?? {})} />
              </Suspense>
            }
          />
        ))}
      </Route>
      {/*
       * Phase 2 (per-org) setup wizard. Lives on its own auth-gated
       * shell so the host `AppShell` (sidebar) cannot mount behind the
       * wizard and tempt users to navigate away from the gate.
       * - `/org-setup/new` — bootstrap-new flow when the user has no
       *   org memberships at all.
       * - `/orgs/:orgSlug/setup` — bootstrap an existing un-bootstrapped
       *   org. Sibling of the `orgs/:orgSlug` subtree below but
       *   mounted outside `OrgSlugResolver` so it skips `AppShell`.
       */}
      <Route element={<AuthedWizardShell />}>
        {/* Anti-accidental-org decision screen. A user with zero
            memberships is redirected here by `OrgGate` so they
            explicitly pick join-vs-create before reaching the
            create-org form below. */}
        <Route path="org-setup" element={<OrgSetupWelcome />} />
        <Route path="org-setup/new" element={<OrgSetupPage mode="new" />} />
        {/* `:stage` lives in the URL so the wizard's progress is
            deep-linkable and browser back/forward jumps between stages
            instead of unmounting the whole flow. A bare
            `/orgs/:orgSlug/setup` redirects to the existing-mode entry
            stage (`runloop`). A bookmark on a retired stage (`api-keys`,
            `integrations`) is not a valid `:stage`, so the wizard snaps it
            to that same entry stage. Plugins are derived from the org's
            work type and managed later from Admin -> Plugins, and model
            provider keys live on Admin -> Model Providers, so the wizard
            carries neither step. */}
        <Route
          path={`${ORG_PATH_PREFIX}/:orgSlug/setup`}
          element={<Navigate to="runloop" replace />}
        />
        <Route
          path={`${ORG_PATH_PREFIX}/:orgSlug/setup/:stage`}
          element={<OrgSetupExistingRoute />}
        />
        {/* Compat for pre-`/orgs` wizard deep links. Mounted on the wizard
            shell (not the app shell) so the redirect doesn't flash the
            sidebar; ranks above the app shell's `:legacyOrgSlug/*`. */}
        <Route path=":legacyOrgSlug/setup/*" element={<LegacyOrgPathRedirect />} />
      </Route>
      <Route element={<AuthedShell />}>
        {/* Root-level authed routes that are NOT org-scoped. A new route
            here must add its first segment to `RESERVED_ROOT_SEGMENTS`
            in `@reflex/shared/routes`. The index route (`/`) lives at the
            top of this route table on `RootIndexRoute` — signed-in users
            reach the shell through the bare `/orgs` redirect below. */}
        <Route path="onboard" element={<UserOnboardingPage />} />
        {/* Personal Profile and Settings, plus plugin-contributed account
            routes (e.g. /tutorials), render inside the Account takeover so
            the app sidebar steps aside and the account menu options own the
            aside — same contract as the Configure / Labs takeovers. */}
        <Route element={<AccountShell />}>
          <Route path="profile" element={<ProfilePage />} />
          <Route path="profile/:userId" element={<ProfilePage />} />
          <Route path="settings" element={<SettingsPage />} />
          {accountRootLevelPluginRoutes.map(({ path, component: Component, config }) => (
            <Route
              key={`account-root-${path}`}
              path={asRelativePath(path)}
              element={
                <Suspense fallback={<RouteSuspenseTracker />}>
                  <Component {...(config ?? {})} />
                </Suspense>
              }
            />
          ))}
        </Route>
        <Route path="credentials" element={<CredentialsRedirect />} />
        <Route path="me/secrets" element={<UserSecretsRedirect />} />
        {/*
         * Plugin-contributed root-level routes (e.g. changelog). Mounted
         * outside the `:orgSlug` parent so the URL is `/<path>` rather
         * than `/orgs/:orgSlug/<path>`. Plugins opt in via `rootLevel: true`
         * on the `PluginWebRoute` (or `definePage`). Account root-level routes
         * were split out above into the AccountShell.
         */}
        {nonAccountRootLevelRoutes.map(({ path, component: Component, config }) => (
          <Route
            key={`root-${path}`}
            path={asRelativePath(path)}
            element={
              <Suspense fallback={<RouteSuspenseTracker />}>
                <Component {...(config ?? {})} />
              </Suspense>
            }
          />
        ))}
        <Route
          path="admin"
          element={
            <RequireSuperAdmin>
              <AdminShell />
            </RequireSuperAdmin>
          }
        >
          <Route index element={<Navigate to="monitor" replace />} />
          <Route path="monitor" element={<MonitorPage />} />
          <Route path="monitor/orgs/:organizationId" element={<MonitorOrgDetailPage />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="users/:userId" element={<AdminUserDetailPage />} />
          <Route path="organizations" element={<AdminOrganizationsPage />} />
          <Route path="organizations/:organizationId" element={<AdminOrganizationDetailPage />} />
          <Route path="companies" element={<AdminCompaniesPage />} />
          <Route path="companies/:billingEntityId" element={<AdminBillingEntityDetailPage />} />
          <Route
            path="billing-entities/:billingEntityId"
            element={<LegacyAdminBillingEntityRedirect />}
          />
          <Route path="appearance" element={<AdminAppearancePage />} />
          <Route path="settings" element={<AdminServiceSettingsPage />} />
          <Route path="email" element={<AdminEmailSettingsPage />} />
          <Route path="maintenance" element={<AdminMaintenancePage />} />
          <Route path="flags" element={<AdminFlagsPage />} />
          <Route path="auth" element={<AdminAuthPage />} />
          <Route path="service-secrets" element={<ServiceSecretsPage />} />
          <Route path="webhooks" element={<AdminWebhooksPage />} />
          <Route path="plugins" element={<AdminPluginsPage />} />
          <Route path="plugins/:pluginName" element={<AdminPluginDetailPage />} />
          <Route path="plugins/:pluginName/setup" element={<AdminPluginSetupPage />} />
          <Route
            path="database"
            element={
              <Suspense fallback={<RouteSuspenseTracker />}>
                <DatabasePage />
              </Suspense>
            }
          />
          <Route
            path="seeds"
            element={
              <Suspense fallback={<RouteSuspenseTracker />}>
                <SeedsPage />
              </Suspense>
            }
          />
          <Route
            path="notifications"
            element={
              <Suspense fallback={<RouteSuspenseTracker />}>
                <AdminNotificationsPage />
              </Suspense>
            }
          />
        </Route>

        {/*
         * Legacy redirects: bookmarked slug-less URLs from before the
         * org-slug migration forward to `/orgs/:activeSlug/<original-path>`.
         * Anything not listed falls through to the slug-ful compat
         * redirect below or the 404.
         */}
        {LEGACY_ROOT_REDIRECT_PATHS.map((path) => (
          <Route key={path} path={path} element={<LegacyRedirect />} />
        ))}

        {/* Bare `/orgs` has no slug to resolve — land on the active org. */}
        <Route path={ORG_PATH_PREFIX} element={<OrgRootRedirect />} />

        {/*
         * Org-scoped routes. The `orgs/:orgSlug` parent route mounts
         * `OrgSlugResolver`, which validates the slug against the user's
         * memberships and syncs the active organization in `useAuth` state.
         */}
        <Route path={`${ORG_PATH_PREFIX}/:orgSlug`} element={<OrgSlugResolver />}>
          <Route index element={<Navigate to="runs" replace />} />
          {/* `orgs/:orgSlug/setup` lives on `AuthedWizardShell` above — it
           * shares the URL namespace but renders without the sidebar. */}
          <Route path="inbox" element={<InboxPage />} />
          <Route path="runs" element={<AgentsPage />} />
          <Route path="agents/:agentId" element={<AgentsPage />} />
          {/*
           * Configuration subtree: agent config, the Organization drill, and
           * the Security pages render inside the Configure takeover
           * (`ConfigureShell`). URLs are unchanged; only the frame is. The
           * legacy integration redirects stay in here so their targets
           * resolve without a sidebar flash on the way through.
           */}
          <Route element={<ConfigureShell />}>
            {/* ── Canonical Configure routes ─────────────────────────── */}

            {/* Build */}
            <Route path="configure/build/agents">
              <Route index element={<Navigate to="personas" replace />} />
              <Route path=":section" element={<AgentsHubPage />} />
            </Route>
            <Route path="configure/build/environments" element={<BlueprintsPage />} />
            <Route path="configure/build/environments/new" element={<BlueprintsPage create />} />
            <Route path="configure/build/environments/base-image" element={<BaseImageRedirect />} />
            <Route path="configure/build/environments/:blueprintId" element={<BlueprintsPage />} />

            {/* People */}
            <Route path="configure/people/roles" element={<OrganizationRolesPage />} />
            <Route path="configure/people/roles/:roleId" element={<OrganizationRolesPage />} />
            <Route path="configure/people/teams" element={<OrganizationTeamsPage />} />
            <Route path="configure/people/teams/:teamId" element={<OrganizationTeamsPage />} />
            <Route path="configure/people/teams/:teamId/secrets" element={<TeamSecretsPage />} />
            <Route
              path="configure/people/teams/:teamId/model-providers"
              element={<TeamModelProvidersPage />}
            />
            <Route path="configure/people/members" element={<OrganizationMembersPage />} />

            {/* Usage and Costs — host-owned Sharing policy page; the other
                rows in the group are plugin-contributed. */}
            <Route path="configure/usage/sharing" element={<SharingSettingsPage />} />

            {/* Company — the level above the organization: one contract, one
                plan, one identity provider. The active org is the door the
                server authorizes through, so the page stays in the org
                subtree. Security (`configure/company/security`) is
                contributed by plugin-auth-oidc. */}
            <Route path="configure/company" element={<CompanyPage />} />

            {/* Company → Security before the company URL namespace existed.
                Without this route the path falls to the `configure/organization`
                block's `:section` below and renders the Organization hub, which
                is what a saved link would have hit on a cold load even before
                the move. Declared flat and in full for legibility; React Router
                scores the flattened path, so a static `enterprise-authorization`
                outranks `:section` from inside that block too.

                Unconditional on purpose, including while the plugin manifest
                is still loading: a cold deep link is the case this exists for,
                and the row below catches the deployment where the target does
                not exist. */}
            <Route
              path={LEGACY_ENTERPRISE_AUTHORIZATION_PATH}
              element={<Navigate to={`../${COMPANY_SECURITY_PATH}`} replace />}
            />

            {/* Security is plugin-contributed, so without plugin-auth-oidc
                nothing serves this path and both a saved link and the redirect
                above would land on 404. Overview is the nearest true thing:
                host-owned, always mounted, and it links to Security itself.
                Mounted only when no plugin claimed the path, so the real page
                always wins. */}
            {!companySecurityContributed && (
              <Route
                path={COMPANY_SECURITY_PATH}
                element={<Navigate to="../configure/company" replace />}
              />
            )}

            {/* Organization */}
            <Route path="configure/organization">
              <Route index element={<Navigate to="general" replace />} />
              <Route path="service-accounts/:id" element={<OrganizationHubPage />} />
              <Route path=":section" element={<OrganizationHubPage />} />
            </Route>

            {/* ── Redirects from old host paths to new configure paths ─ */}
            <Route
              path="blueprints"
              element={<Navigate to="../configure/build/environments" replace />}
            />
            <Route
              path="blueprints/new"
              element={<Navigate to="../configure/build/environments/new" replace />}
            />
            <Route
              path="blueprints/base-image"
              element={<Navigate to="../configure/build/environments/base-image" replace />}
            />
            <Route path="blueprints/:blueprintId" element={<LegacyBlueprintRedirect />} />
            <Route
              path="org/agent-models"
              element={<Navigate to="../configure/build/agents/models" replace />}
            />
            <Route
              path="org/integrations"
              element={<Navigate to="../configure/connect/integrations" replace />}
            />
            <Route
              path="org/plugins"
              element={<Navigate to="../configure/connect/integrations" replace />}
            />
            <Route path="org/plugins/:pluginName" element={<LegacyOrgPluginDetailRedirect />} />
            <Route
              path="org/settings"
              element={<Navigate to="../configure/organization/general" replace />}
            />
            <Route
              path="org/members"
              element={<Navigate to="../configure/people/members" replace />}
            />
            <Route path="org/teams" element={<Navigate to="../configure/people/teams" replace />} />
            <Route path="org/teams/:teamId" element={<LegacyTeamDetailRedirect />} />
            <Route path="org/teams/:teamId/secrets" element={<LegacyTeamSecretsRedirect />} />
            <Route
              path="org/teams/:teamId/model-providers"
              element={<LegacyTeamModelProvidersRedirect />}
            />
            <Route path="org/roles" element={<Navigate to="../configure/people/roles" replace />} />
            <Route path="org/roles/:roleId" element={<LegacyRoleDetailRedirect />} />
            <Route
              path="org/service-accounts"
              element={<Navigate to="../configure/organization/service-accounts" replace />}
            />
            <Route
              path="org/service-accounts/:id"
              element={<LegacyServiceAccountDetailRedirect />}
            />
            <Route
              path="org/secrets"
              element={
                <Navigate to="../configure/connect/credentials/organization/secrets" replace />
              }
            />
            <Route
              path="org/secrets/storage"
              element={
                <Navigate
                  to="../configure/connect/credentials/organization/secrets/storage"
                  replace
                />
              }
            />
            <Route
              path="org/model-providers"
              element={
                <Navigate
                  to="../configure/connect/credentials/organization/model-providers"
                  replace
                />
              }
            />
            <Route
              path="security/model-providers"
              element={
                <Navigate to="../configure/connect/credentials/personal/model-providers" replace />
              }
            />
            <Route
              path="security/secrets"
              element={<Navigate to="../configure/connect/credentials/personal/secrets" replace />}
            />
            <Route
              path="security/api-keys"
              element={<Navigate to="../configure/connect/credentials/personal/api-keys" replace />}
            />
            <Route
              path="security/sharing"
              element={<Navigate to="../configure/connect/sharing/personal" replace />}
            />

            {/* ── Redirects from old plugin paths to new configure paths ─ */}
            <Route
              path="mcp-servers"
              element={<Navigate to="../configure/connect/mcp/servers" replace />}
            />
            <Route
              path="ai-gateways"
              element={<Navigate to="../configure/connect/ai-gateway" replace />}
            />
            <Route
              path="tailscale"
              element={<Navigate to="../configure/connect/tailscale" replace />}
            />
            <Route path="org/billing" element={<Navigate to="../configure/usage/plan" replace />} />
            <Route
              path="org/billing/usage"
              element={<Navigate to="../configure/usage/sandbox" replace />}
            />
            <Route
              path="org/costs"
              element={<Navigate to="../configure/usage/model-costs" replace />}
            />
            <Route path="org/costs/users/:userId" element={<LegacyCostsUserRedirect />} />
            <Route path="org/costs/keys/:secretId" element={<LegacyCostsKeyRedirect />} />
            <Route path="org/costs/keys/:secretId/records" element={<LegacyCostsKeyRedirect />} />
            <Route
              path="org/costs/keys/:secretId/records/:category"
              element={<LegacyCostsKeyRedirect />}
            />
            <Route path="org/costs/providers/:provider" element={<LegacyCostsProviderRedirect />} />
            <Route
              path="org/costs/categories/:category"
              element={<LegacyCostsCategoryRedirect />}
            />
            <Route
              path="org/security"
              element={<Navigate to="../configure/network-policies" replace />}
            />
            <Route
              path="agent-personas"
              element={<Navigate to="../configure/build/agents/personas" replace />}
            />
            <Route
              path="agent-personas/new"
              element={<Navigate to="../configure/build/agents/personas/new" replace />}
            />
            <Route path="agent-personas/:id" element={<LegacyAgentPersonaDetailRedirect />} />
            <Route path="agent-personas/:id/edit" element={<LegacyAgentPersonaDetailRedirect />} />
            <Route
              path="org/integrations/sentry/:projectId"
              element={<LegacyOrgSentryProjectRedirect />}
            />
            <Route path="org/integrations/:provider" element={<LegacyOrgIntegrationRedirect />} />

            {/* ── Pages that stay at their current path ──────────────── */}
            {/* The agent type catalogue moved to the docs corpus. */}
            <Route
              path="agent-types/*"
              element={<Navigate to="/docs/concepts/agent-types" replace />}
            />
            {/* Security and Organization overview hubs (not in target IA,
                kept so drill-group landing links resolve). */}
            <Route path="security" element={<SecurityOverviewPage />} />
            <Route path="org" element={<OrganizationOverviewPage />} />

            {/* ── Legacy integration redirects ───────────────────────── */}
            <Route
              path="integrations"
              element={<Navigate to="../configure/connect/integrations" replace />}
            />
            <Route
              path="integrations/mcp-setup"
              element={<Navigate to="../configure/connect/mcp/setup" replace />}
            />
            <Route
              path="integrations/github"
              element={<Navigate to="../configure/connect/plugins/github" replace />}
            />
            <Route
              path="pylon"
              element={<Navigate to="../configure/connect/plugins/pylon" replace />}
            />
            <Route
              path="linear"
              element={<Navigate to="../configure/connect/plugins/linear" replace />}
            />
            <Route
              path="slack"
              element={<Navigate to="../configure/connect/plugins/slack" replace />}
            />
            <Route
              path="sentry"
              element={<Navigate to="../configure/connect/plugins/sentry" replace />}
            />
            <Route path="sentry/:projectId" element={<LegacySentryProjectRedirect />} />
            <Route
              path="pr-reviews"
              element={<Navigate to="../configure/connect/plugins/github" replace />}
            />

            {/* ── Plugin-contributed Configure routes ─────────────────── */}
            {configurePluginRoutes.map(({ path, component: Component, config }) => (
              <Route
                key={path}
                path={asRelativePath(path)}
                element={
                  <Suspense fallback={<RouteSuspenseTracker />}>
                    <Component {...(config ?? {})} />
                  </Suspense>
                }
              />
            ))}
          </Route>
          {/*
           * The Connect takeover: Credentials, Sharing, MCP, AI Gateway,
           * and Integrations render inside this shell. URLs stay at
           * /configure/connect/... but the frame is the Connect takeover.
           */}
          <Route element={<ConnectShell />}>
            <Route path="configure/connect/integrations" element={<IntegrationsPage />} />
            <Route
              path="configure/connect/integrations/:provider"
              element={<ConnectIntegrationPluginRedirect />}
            />
            <Route path="configure/connect/plugins/:pluginName" element={<PluginDetailPage />} />
            <Route path="configure/connect/mcp">
              <Route index element={<Navigate to="servers" replace />} />
              <Route path=":section" element={<McpHubPage />} />
            </Route>
            <Route path="configure/connect/credentials">
              <Route index element={<Navigate to="personal/model-providers" replace />} />
              <Route path="team/:teamId/:section" element={<CredentialsHubPage />} />
              <Route path="organization/secrets/storage" element={<SecretStorageDetailPage />} />
              <Route path=":scope/:section" element={<CredentialsHubPage />} />
            </Route>
            <Route path="configure/connect/sharing">
              <Route index element={<Navigate to="personal" replace />} />
              <Route path=":scope" element={<SharingHubPage />} />
            </Route>

            {/* ── Plugin-contributed Connect routes ──────────────────── */}
            {connectPluginRoutes.map(({ path, component: Component, config }) => (
              <Route
                key={path}
                path={asRelativePath(path)}
                element={
                  <Suspense fallback={<RouteSuspenseTracker />}>
                    <Component {...(config ?? {})} />
                  </Suspense>
                }
              />
            ))}
          </Route>
          {/*
           * The Labs takeover: chat, the misc contents, and the plugins
           * overview share one frame, same contract as the settings and
           * automations takeovers. URLs are unchanged; only the frame is.
           */}
          <Route element={<LabsShell />}>
            <Route path="misc" element={<MiscOverviewPage />} />
            <Route
              path="misc/mcp-setup"
              element={<Navigate to="../configure/connect/mcp/setup" replace />}
            />
            <Route path="plugins" element={<PluginsOverviewPage />} />
            <Route path="plugins/:pluginName" element={<LegacyPluginDetailRedirect />} />
            {labsPluginRoutes.map(({ path, component: Component, config }) => (
              <Route
                key={path}
                path={asRelativePath(path)}
                element={
                  <Suspense fallback={<RouteSuspenseTracker />}>
                    <Component {...(config ?? {})} />
                  </Suspense>
                }
              />
            ))}
          </Route>
          {workPluginRoutes.map(({ path, component: Component, config }) => (
            <Route
              key={path}
              path={asRelativePath(path)}
              element={
                <Suspense fallback={<RouteSuspenseTracker />}>
                  <Component {...(config ?? {})} />
                </Suspense>
              }
            />
          ))}
          {/*
           * Org-scoped account plugin routes (e.g. /notifications) render
           * inside the Account takeover, same frame contract as the
           * root-level AccountShell above. URLs are unchanged; only the
           * frame is.
           */}
          {accountPluginRoutes.length > 0 && (
            <Route element={<AccountShell />}>
              {accountPluginRoutes.map(({ path, component: Component, config }) => (
                <Route
                  key={path}
                  path={asRelativePath(path)}
                  element={
                    <Suspense fallback={<RouteSuspenseTracker />}>
                      <Component {...(config ?? {})} />
                    </Suspense>
                  }
                />
              ))}
            </Route>
          )}
          {/*
           * The flows surface renders inside the Automations takeover, the
           * same frame contract as the settings takeover above. URLs are
           * unchanged; only the frame is.
           */}
          {automationsPluginRoutes.length > 0 && (
            <Route element={<AutomationsShell />}>
              {automationsPluginRoutes.map(({ path, component: Component, config }) => (
                <Route
                  key={path}
                  path={asRelativePath(path)}
                  element={
                    <Suspense fallback={<RouteSuspenseTracker />}>
                      <Component {...(config ?? {})} />
                    </Suspense>
                  }
                />
              ))}
            </Route>
          )}
          <Route path="*" element={pluginsLoading ? <RouteSuspenseTracker /> : <NotFoundPage />} />
        </Route>

        {/*
         * Compat for pre-`/orgs` org URLs (`/acme/agents/x`). Every static
         * route above outranks this dynamic catch-all, so only paths whose
         * first segment is not a known route reach it.
         */}
        <Route path=":legacyOrgSlug/*" element={<LegacyOrgPathRedirect />} />

        <Route path="*" element={pluginsLoading ? <RouteSuspenseTracker /> : <NotFoundPage />} />
      </Route>
    </Routes>
  );
}

/**
 * Top-level route dispatcher. Treats `/docs` and `/demos` as reserved namespaces
 * that render in completely separate route trees, preventing those paths from
 * ever competing with the dynamic pre-`/orgs` compat catch-all
 * (`:legacyOrgSlug/*`).
 *
 * The check is case-insensitive so `/Docs` or `/DOCS` also route to docs.
 *
 * `/docs` - Always renders in the standalone `DocsLayout` shell via
 * `DocsPublicRoutes`, regardless of authentication state. The demos catalog
 * at `/docs/demos` and individual demos at `/docs/demos/:slug` render here.
 *
 * `/demos` - Redirects to `/docs/demos[/...]` where demos render inside the
 * docs shell (PRD-G14/G16). Keeps old bookmarks working.
 */
export function AppRoutes() {
  const { pathname } = useLocation();
  const lower = pathname.toLowerCase();
  const isDocsRoute = lower === '/docs' || lower.startsWith('/docs/');
  const isDemosRoute = lower === '/demos' || lower.startsWith('/demos/');

  if (isDocsRoute) {
    return <DocsPublicRoutes />;
  }

  // Redirect /demos paths to canonical /docs/demos URLs
  if (isDemosRoute) {
    return <DemosRedirectRoutes />;
  }

  return (
    <PluginClientBridge>
      <MainAppRoutes />
    </PluginClientBridge>
  );
}

function AccountPreferencesSync() {
  useAccountPreferencesSync();
  return null;
}

export default function App() {
  const ErrorBoundary = getErrorReporter().ErrorBoundary;
  return (
    <MotionConfig reducedMotion="user">
      <ThemeProvider attribute="class" defaultTheme="system" storageKey="reflex-theme">
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <ColorThemeProvider>
              <AccountPreferencesSync />
              <TooltipProvider>
                <ErrorBoundary
                  fallback={
                    <div
                      role="alert"
                      className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-background text-foreground"
                    >
                      <PanelTitle as="h1">Something went wrong.</PanelTitle>
                      <Button type="button" onClick={() => window.location.reload()}>
                        Reload page
                      </Button>
                    </div>
                  }
                >
                  <BrowserRouter>
                    <NuqsAdapter defaultOptions={NUQS_ADAPTER_DEFAULT_OPTIONS}>
                      <TopProgressBar />
                      <AppRoutes />
                    </NuqsAdapter>
                  </BrowserRouter>
                </ErrorBoundary>
                <Toaster />
              </TooltipProvider>
            </ColorThemeProvider>
          </AuthProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </MotionConfig>
  );
}
